import type { Feature, FeatureCollection, Point, Polygon } from "geojson";

import type { DroneAirspace, DroneAirspaceProps, Phase, SmallDrones, TerrainDrones, TerrainReport } from "../../api/types";
import { citedCauses } from "../../drones";
import { t } from "../../i18n/i18n";
import type { DroneControlKey } from "../../state/ui";

/** Calques du groupe « Drones » (chantier « Drones », D11 et D13), sans moteur de carte : tri des éléments du calque
 *  servi par le serveur (VAA, ROZ drones, NAI) selon les lignes cochées et le temps affiché, éléments drones de
 *  l'analyse du terrain (zones de mise en œuvre, cellules masquées à nos drones), état des lignes du panneau Calques
 *  (compteur, ligne masquée, blocage et action attendue), textes de la section « Drones » de l'analyse. Module pur,
 *  essayé seul. */

/** Cible de la ligne « Ennemis » du panneau Calques : désignée par la ligne des NAI tant qu'aucun ennemi n'est placé
 *  (D13). */
export const ENEMIES_ROW_TARGET = "layers-eni";
/** Cible « Analyser le terrain » de la vue Analyse du terrain : désignée par la ligne des éléments drones de
 *  l'analyse tant qu'aucune analyse (avec sa section « Drones ») n'est affichée. */
export const TERRAIN_ANALYZE_TARGET = "terrain-analyze";

export type AirspaceKind = DroneAirspaceProps["kind"];

/** Ligne du panneau Calques qui montre chaque nature d'élément. */
export const KIND_CONTROL: Readonly<Record<AirspaceKind, DroneControlKey>> = {
  vaa: "droneVaa",
  roz: "droneRoz",
  nai: "droneNai",
};

/** Opacité d'un élément hors du temps affiché (D11) : estompé, jamais caché. */
export const INACTIVE_OPACITY = 0.35;

/** Clé de requête du calque : dossier, version (une écriture le recalcule) et OPO (rotation CENTAC). */
export function airspaceKey(exerciseId: string | null, version: number | null, opo: number | null): (string | number | null)[] {
  return ["drone-airspace", exerciseId, version, opo];
}

/** L'élément est-il actif au temps affiché ? */
export function activeAt(props: Pick<DroneAirspaceProps, "phases">, phase: Phase): boolean {
  return props.phases.includes(phase);
}

/** Nombre d'éléments par nature (surfaces seulement : chaque élément a aussi son point d'étiquette). */
export function airspaceCounts(data: DroneAirspace | undefined | null): Record<AirspaceKind, number> {
  const out: Record<AirspaceKind, number> = { vaa: 0, roz: 0, nai: 0 };
  for (const f of data?.features ?? []) if (f.properties.role === "area") out[f.properties.kind] += 1;
  return out;
}

export interface AirspaceLabel {
  key: string;
  kind: AirspaceKind;
  at: [number, number];
  label: string;
  lines: string[];
  active: boolean;
}

/** Surfaces à dessiner (propriété `active` posée selon le temps affiché) et étiquettes, pour les natures cochées. */
export function shownAirspace(
  data: DroneAirspace | undefined | null,
  shown: Readonly<Record<AirspaceKind, boolean>>,
  phase: Phase,
): { areas: FeatureCollection<Polygon, DroneAirspaceProps & { active: boolean }>; labels: AirspaceLabel[] } {
  const areas: Feature<Polygon, DroneAirspaceProps & { active: boolean }>[] = [];
  const labels: AirspaceLabel[] = [];
  for (const f of data?.features ?? []) {
    const p = f.properties;
    if (!shown[p.kind]) continue;
    const active = activeAt(p, phase);
    if (p.role === "area" && f.geometry.type === "Polygon") {
      areas.push({ type: "Feature", properties: { ...p, active }, geometry: f.geometry });
    } else if (p.role === "label" && f.geometry.type === "Point") {
      const [lon, lat] = (f.geometry as Point).coordinates;
      labels.push({ key: p.key, kind: p.kind, at: [lon!, lat!], label: p.label, lines: p.lines, active });
    }
  }
  // VAA dessous, puis ROZ, NAI dessus : une NAI dans le VAA reste lisible.
  const order: Record<AirspaceKind, number> = { vaa: 0, roz: 1, nai: 2 };
  areas.sort((a, b) => order[a.properties.kind] - order[b.properties.kind]);
  return { areas: { type: "FeatureCollection", features: areas }, labels };
}

/** Un élément coché est-il estompé au temps affiché (la légende le dit) ? */
export function anyInactive(data: DroneAirspace | undefined | null, shown: Readonly<Record<AirspaceKind, boolean>>, phase: Phase): boolean {
  return (data?.features ?? []).some((f) => f.properties.role === "area" && shown[f.properties.kind] && !activeAt(f.properties, phase));
}

/** Section « Drones » d'un rapport d'analyse (absente d'un rapport d'un serveur antérieur, null en CENTAC). */
export function terrainDronesOf(report: TerrainReport | null | undefined): TerrainDrones | null {
  return report?.drones ?? null;
}

/** Éléments drones de l'analyse du terrain sur la carte : zones de mise en œuvre numérotées (points) et cellules de la
 *  grille de l'analyse masquées à nos drones par le relief (carrés de la maille du rapport). */
export function terrainDroneFeatures(report: TerrainReport | null | undefined): {
  sites: FeatureCollection<Point, { n: number }>;
  masked: FeatureCollection<Polygon>;
} | null {
  const drones = terrainDronesOf(report);
  if (!report || !drones?.available) return null;
  const hLat = (report.cell_span.dlat / 2) * 0.94;
  const hLon = (report.cell_span.dlon / 2) * 0.94;
  return {
    sites: {
      type: "FeatureCollection",
      features: drones.launch_sites.map((s, i) => ({
        type: "Feature",
        properties: { n: i + 1 },
        geometry: { type: "Point", coordinates: [s.lon, s.lat] },
      })),
    },
    masked: {
      type: "FeatureCollection",
      features: drones.masked.map(([lon, lat]) => ({
        type: "Feature",
        properties: {},
        geometry: {
          type: "Polygon",
          coordinates: [[
            [lon - hLon, lat - hLat],
            [lon + hLon, lat - hLat],
            [lon + hLon, lat + hLat],
            [lon - hLon, lat + hLat],
            [lon - hLon, lat - hLat],
          ]],
        },
      })),
    },
  };
}

/** Ce que montrent les éléments drones de l'analyse : rien sans section disponible. */
export function terrainDroneCount(report: TerrainReport | null | undefined): number {
  const drones = terrainDronesOf(report);
  return drones?.available ? drones.launch_sites.length : 0;
}

/** Préalable manquant d'une ligne du groupe « Drones » : l'ennemi à placer (NAI), le terrain à analyser (éléments de
 *  l'analyse) ; null quand la ligne se coche. */
export type DroneRowBlock = "enemy" | "terrain" | null;

export interface DroneRow {
  key: DroneControlKey;
  /** Ligne présente dans le panneau (rien d'inutile : pas de ROZ au GTIA, ni VAA ni NAI au corps). */
  present: boolean;
  count: number | undefined;
  /** Calque sans objet tant que le dossier n'est pas créé ou que le calcul n'est pas revenu. */
  disabled: boolean;
  blocked: DroneRowBlock;
}

/** Échelons qui ont une ROZ drones (D11) ; le corps n'a ni VAA ni NAI (ses modes d'action sont propres à l'ordre de
 *  corps). */
const ROZ_LEVELS = new Set(["BRIGADE", "DIVISION", "CORPS"]);

/** Lignes du groupe « Drones » du panneau Calques. `level` : niveau du dispositif affiché ; `centac` : rotation CENTAC
 *  (le groupe entier est masqué, rien de nouveau) ; `enemies` : unités ennemies placées au temps affiché ; `report` :
 *  analyse du terrain affichée. */
export function droneRows({
  level,
  centac,
  saved,
  airspace,
  enemies,
  report,
}: {
  level: string | null | undefined;
  centac: boolean;
  saved: boolean;
  airspace: DroneAirspace | undefined | null;
  enemies: number;
  report: TerrainReport | null | undefined;
}): DroneRow[] {
  if (centac || !level) return [];
  const counts = airspaceCounts(airspace);
  const corps = level === "CORPS";
  const waiting = !saved || !airspace;
  const drones = terrainDronesOf(report);
  return [
    { key: "droneVaa", present: !corps, count: waiting ? undefined : counts.vaa, disabled: waiting || counts.vaa === 0, blocked: null },
    { key: "droneRoz", present: ROZ_LEVELS.has(level), count: waiting ? undefined : counts.roz, disabled: waiting || counts.roz === 0, blocked: null },
    {
      key: "droneNai",
      present: !corps,
      count: waiting ? undefined : counts.nai,
      // Sans ennemi placé, la ligne désigne la ligne « Ennemis » (D13) ; avec lui, rien à cocher tant qu'aucune NAI.
      disabled: enemies > 0 && (waiting || counts.nai === 0),
      blocked: enemies === 0 ? "enemy" : null,
    },
    {
      key: "droneTerrain",
      present: true,
      count: drones?.available ? drones.launch_sites.length : undefined,
      disabled: false,
      // Sans analyse (ou rapport d'un serveur antérieur), la ligne désigne « Analyser le terrain ».
      blocked: drones ? null : "terrain",
    },
  ];
}

/** Valeur de la ligne « Petits drones » : « aptes au vol », « limités : vent 40 km/h, pluie (autonomie réduite de
 *  moitié) », « interdits : vent 50 km/h » — mêmes causes et même règle que la ligne de l'aperçu Météo. */
export function smallDronesValue(small: SmallDrones): string {
  if (small.status === "ok") return t("aptes au vol");
  const label = (cause: SmallDrones["causes"][number]) =>
    cause === "wind"
      ? t("vent {kmh} km/h", { kmh: small.wind_kmh })
      : cause === "icing"
        ? t("givrage")
        : cause === "rain"
          ? t("pluie (autonomie réduite de moitié)")
          : cause === "cold"
            ? t("froid (batteries)")
            : t("chaleur (batteries)");
  const grounded = small.status === "suspended";
  const causes = citedCauses(small) // même règle que l'aperçu Météo (seuil unique de `drones.ts`)
    .map(label)
    .join(", ");
  if (grounded) return causes ? t("interdits : {causes}", { causes }) : t("interdits");
  return causes ? t("limités : {causes}", { causes }) : t("limités");
}

/** Phrase des couverts contre les drones ennemis (part de forêt et de bâti de la zone). */
export function droneCoverText(pct: number): string {
  if (pct >= 40) return t("Couverts abondants : dissimulation aisée aux drones ennemis, à condition de rester sous les bois et dans le bâti.");
  if (pct >= 15) return t("Couverts discontinus : itinéraires et haltes sous couvert à planifier face aux drones ennemis.");
  return t("Terrain découvert : forte exposition aux drones ennemis ; dispersion, camouflage et lutte anti-drones prioritaires.");
}
